import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import { ModalBase, PageLoader, VehicleClass, colors } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { CalculatorResponseDtoSchemaType } from 'components/forms/CarrierFormFactory/types';
import { PricingAdjustmentTypeEnum } from 'utils/data/enums';
import { useOperatorsOrdersPricingDetailsQuery } from 'api/queries/operators';
import { useOperatorCurrency, useOperatorDistanceUnit } from '@/api/queries/users';
import {
  getAttributesTableData,
  getCostsFirstTableData,
  getCostsSecondTableData,
} from './tableDataObjects';

interface Props {
  open: boolean;
  bookingNumber: string;
  onClose: () => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function CostsTransparencyTable({ data }: { data: CalculatorResponseDtoSchemaType }) {
  const { t, i18n } = useTranslation();
  const currency = useOperatorCurrency();

  const getAdjustmentValue = () => {
    const calculatedAdjustmentValue = (data.finalPriceInclVat / 100) * data.adjustmentPct;
    return Math.round(calculatedAdjustmentValue * 100) / 100;
  };

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography mb='20px'>
            {t('modals.pricingTransparency.costs.table.vehicleCosts')}
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '8px',
              border: `1px solid ${colors.primaryBlue4}`,
            }}
          >
            <Table
              sx={{
                '& .MuiTableRow-root:last-of-type': {
                  backgroundColor: colors.uiBlue50,
                },
              }}
            >
              <TableBody>
                {getCostsFirstTableData(data, currency, i18n.language).map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>
                      {t(`modals.pricingTransparency.costs.table.header.${row.label}`)}
                    </TableCell>
                    <TableCell>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography mb='20px'>{t('modals.pricingTransparency.costs.table.rideCosts')}</Typography>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '8px',
              border: `1px solid ${colors.primaryBlue4}`,
            }}
          >
            <Table>
              <TableBody>
                {getCostsSecondTableData(data, getAdjustmentValue, currency, i18n.language).map(
                  (row) => (
                    <TableRow key={row.label}>
                      <TableCell>
                        {t(`modals.pricingTransparency.costs.table.header.${row.label}`)}
                      </TableCell>
                      <TableCell>{row.value}</TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
}

export function CostsTransparencyModal({
  open,
  onClose,
  setOpen,
  data,
  title,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  data: CalculatorResponseDtoSchemaType;
  title?: string;
}) {
  const { t } = useTranslation();

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      setOpen={setOpen}
      title={title ?? t('modals.pricingTransparency.title').toString()}
      maxWidth='sm'
      fullWidth
    >
      <Box
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          '& .MuiTableRow-root:nth-of-type(odd)': { backgroundColor: colors.primaryBlue5 },
          '& .MuiTableRow-root .MuiTableCell-root:first-of-type': { fontWeight: 500 },
          '& .MuiTableCell-root': { borderBottom: 'none' },
        }}
      >
        <CostsTransparencyTable data={data} />
      </Box>
    </ModalBase>
  );
}

export function PricingTransparency({ bookingNumber }: { bookingNumber: string }) {
  const { data: pricingDetails, isSuccess } = useOperatorsOrdersPricingDetailsQuery(bookingNumber);
  const { t, i18n } = useTranslation();
  const distanceUnit = useOperatorDistanceUnit();

  if (!isSuccess)
    return (
      <Box sx={{ my: '3rem' }}>
        <PageLoader />
      </Box>
    );

  return (
    <Grid
      container
      spacing={4}
      p={2}
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        '& .MuiTableRow-root:nth-of-type(odd)': { backgroundColor: colors.primaryBlue5 },
        '& .MuiTableRow-root .MuiTableCell-root:first-of-type': { fontWeight: 500 },
        '& .MuiTableCell-root': { borderBottom: 'none' },
      }}
    >
      <Grid item xs={12} md={6}>
        <Typography mb='20px'>
          {t('modals.pricingTransparency.costs.attributes.table.title')}
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            borderRadius: '8px',
            border: `1px solid ${colors.primaryBlue4}`,
          }}
        >
          <Table>
            <TableBody>
              {getAttributesTableData(pricingDetails, i18n.language, distanceUnit).map((row) => (
                <TableRow key={row.label}>
                  <TableCell>
                    {t(`modals.pricingTransparency.costs.attributes.table.header.${row.label}`)}
                  </TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12}>
        <CostsTransparencyTable
          data={{
            ...pricingDetails,
            costs: {
              adminCost: pricingDetails.adminCost,
              departureCost: pricingDetails.departureCost,
              driversCost: pricingDetails.driversCost,
              fuelCost: pricingDetails.fuelCost,
              vehicleMileageCost: pricingDetails.vehicleMileageCost,
              vehicleTimeCost: pricingDetails.vehicleTimeCost,
            },
            vehicleClassAdjustmentPct: 0,
            vehicleClass: pricingDetails.vehicleClass as VehicleClass,
            adjustmentType: pricingDetails.adjustmentType as PricingAdjustmentTypeEnum,
          }}
        />
      </Grid>
    </Grid>
  );
}

function PricingTransparencyModal({ open, onClose, bookingNumber, setOpen }: Props) {
  const { t } = useTranslation();

  return (
    <ModalBase
      open={open}
      setOpen={setOpen}
      onClose={onClose}
      title={t('modals.pricingTransparency.title').toString()}
      maxWidth='md'
      fullWidth
    >
      <PricingTransparency bookingNumber={bookingNumber} />
    </ModalBase>
  );
}

export default PricingTransparencyModal;
